import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useMsal } from '@azure/msal-react';
import { AppHeader } from '../../shared/components/AppComponents';
import { apiFetch, apiFetchJson } from '../../apiClient';
import './HouseholdCosts.css';

// The API runs with ReferenceHandler.Preserve, so every collection arrives wrapped.
const unwrap = (data) => {
  const arr = Array.isArray(data) ? data : (data?.$values || []);
  return arr.filter(x => x && !x.$ref);
};

const SPLIT_RULES = ['Ownership', 'Loan', 'Even', 'Direct', 'Custom'];

const fmtNOK = (v) => `${Math.round(v || 0).toLocaleString('nb-NO')} kr`;
const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};
const currentYear = () => String(new Date().getFullYear());
const GROCERY_IMPORT_EXAMPLE = `{
  "retailer": "Rema1000",
  "receipts": [{
    "externalReceiptId": "11223344556",
    "storeName": "REMA 1000",
    "purchasedAt": "2026-09-12T17:30:00",
    "totalAmount": 74.80,
    "totalDiscount": 10.00,
    "items": [
      {
        "productName": "Pepsi Max 1,5L",
        "ean": "7044610874806",
        "productGroup": "Brus",
        "quantity": 2,
        "unitPrice": 24.90,
        "amount": 49.80,
        "discount": 10.00
      },
      {
        "productName": "Q Lettmelk 1L",
        "quantity": 1,
        "unitPrice": 25.00,
        "amount": 25.00
      }
    ]
  }]
}`;

const emptyLine = () => ({
  name: '',
  amount: 0,
  period: 'Monthly',
  splitRule: 'Ownership',
  isIncome: false,
  sortOrder: 99
});

function HouseholdCosts() {
  const { t } = useTranslation();
  const { instance } = useMsal();

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('overview');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [groceryRange, setGroceryRange] = useState('month');
  const [groceryPeriod, setGroceryPeriod] = useState(currentPeriod);
  const [groceryYear, setGroceryYear] = useState(currentYear);
  const [grocerySummary, setGrocerySummary] = useState(null);
  const [groceryLoading, setGroceryLoading] = useState(false);
  const [groceryImport, setGroceryImport] = useState(null);
  const [groceryRecategorized, setGroceryRecategorized] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetchJson(instance, '/api/PropertyCosts/summary');
      setSummary({
        people: unwrap(data?.people),
        properties: unwrap(data?.properties).map(p => ({
          ...p,
          lines: unwrap(p.lines),
          ownerships: unwrap(p.ownerships)
        })),
        householdLines: unwrap(data?.householdLines),
        totals: data?.totals || {},
        settlement: data?.settlement || null
      });
      setError(null);
    } catch (err) {
      console.error('Failed to load cost summary', err);
      setError(t('householdCosts.loadError'));
    } finally {
      setLoading(false);
    }
  }, [instance, t]);

  useEffect(() => { load(); }, [load]);

  const loadGroceries = useCallback(async () => {
    try {
      setGroceryLoading(true);
      const period = groceryRange === 'ytd' ? `${groceryYear}-YTD` : groceryPeriod;
      const data = await apiFetchJson(instance, `/api/Grocery/summary?period=${period}`);
      setGrocerySummary({
        ...data,
        retailers: unwrap(data?.retailers),
        categories: unwrap(data?.categories).map(category => ({
          ...category,
          subcategories: unwrap(category.subcategories)
        }))
      });
    } catch (err) {
      console.error('Failed to load grocery summary', err);
      setError(t('householdCosts.grocery.loadError'));
    } finally {
      setGroceryLoading(false);
    }
  }, [groceryPeriod, groceryRange, groceryYear, instance, t]);

  useEffect(() => {
    if (activeView === 'groceries') loadGroceries();
  }, [activeView, loadGroceries]);

  const people = summary?.people || [];
  const groceryLabel = (name) =>
    t(`householdCosts.grocery.labels.${name}`, { defaultValue: name });

  const handleSeed = async (overwrite) => {
    if (overwrite && !window.confirm(t('householdCosts.confirmOverwrite'))) return;
    try {
      setBusy(true);
      const res = await apiFetch(instance, '/api/PropertyCosts/seed', {
        method: 'POST',
        body: JSON.stringify({ overwrite })
      });
      if (!res.ok) throw new Error(await res.text());
      setError(null);
      await load();
    } catch (err) {
      console.error('Seed failed', err);
      setError(t('householdCosts.seedError'));
    } finally {
      setBusy(false);
    }
  };

  const lineUrl = (line, propertyId) => {
    const isHousehold = !propertyId;
    if (line.id) {
      return isHousehold
        ? `/api/PropertyCosts/household-lines/${line.id}`
        : `/api/PropertyCosts/lines/${line.id}`;
    }
    return isHousehold
      ? '/api/PropertyCosts/household-lines'
      : `/api/PropertyCosts/properties/${propertyId}/lines`;
  };

  const saveLine = async () => {
    const { line, propertyId } = editing;
    if (!line.name.trim()) return;
    try {
      setBusy(true);
      const res = await apiFetch(instance, lineUrl(line, propertyId), {
        method: line.id ? 'PUT' : 'POST',
        body: JSON.stringify({
          name: line.name,
          amount: Number(line.amount) || 0,
          period: line.period,
          splitRule: line.splitRule,
          splitDetailJson: line.splitDetailJson ?? null,
          isIncome: !!line.isIncome,
          sortOrder: line.sortOrder ?? 0,
          category: line.category ?? null,
          notes: line.notes ?? null
        })
      });
      if (!res.ok) throw new Error(await res.text());
      setEditing(null);
      setError(null);
      await load();
    } catch (err) {
      console.error('Save failed', err);
      setError(t('householdCosts.saveError'));
    } finally {
      setBusy(false);
    }
  };

  const deleteLine = async (line, propertyId) => {
    if (!window.confirm(t('householdCosts.confirmDelete', { name: line.name }))) return;
    try {
      setBusy(true);
      const res = await apiFetch(instance, lineUrl(line, propertyId), { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      setError(null);
      await load();
    } catch (err) {
      console.error('Delete failed', err);
      setError(t('householdCosts.deleteError'));
    } finally {
      setBusy(false);
    }
  };

  const importGroceries = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    try {
      setBusy(true);
      setGroceryImport(null);
      const combined = { created: 0, updated: 0, receipts: 0, items: 0 };
      for (const file of files) {
        const payload = JSON.parse(await file.text());
        const result = await apiFetchJson(instance, '/api/Grocery/import', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        Object.keys(combined).forEach(key => {
          combined[key] += result[key] || 0;
        });
      }
      setGroceryImport(combined);
      setError(null);
      await loadGroceries();
    } catch (err) {
      console.error('Grocery receipt import failed', err);
      setError(t('householdCosts.grocery.importError'));
    } finally {
      setBusy(false);
      event.target.value = '';
    }
  };

  const recategorizeGroceries = async () => {
    try {
      setBusy(true);
      const result = await apiFetchJson(instance, '/api/Grocery/recategorize', {
        method: 'POST'
      });
      setGroceryRecategorized(result);
      setError(null);
      await loadGroceries();
    } catch (err) {
      console.error('Grocery recategorization failed', err);
      setError(t('householdCosts.grocery.recategorizeError'));
    } finally {
      setBusy(false);
    }
  };

  // The badge is the whole readability win over the spreadsheet: every row states why it
  // splits the way it does, instead of hiding it inside a cell reference.
  const splitBadge = (rule, ownerships) => {
    let detail = '';
    if (ownerships?.length && (rule === 'Ownership' || rule === 'Loan')) {
      const key = rule === 'Ownership' ? 'ownershipFraction' : 'loanFraction';
      detail = ownerships.map(o => Math.round((o[key] || 0) * 100)).join('/');
    }
    return (
      <span className={`split-badge split-${String(rule).toLowerCase()}`}>
        {t(`householdCosts.rule.${rule}`)}{detail ? ` ${detail}` : ''}
      </span>
    );
  };

  const editorRow = (propertyId, colSpan) => (
    <tr className="line-row editing">
      <td>
        <input
          aria-label={t('householdCosts.line')}
          value={editing.line.name}
          onChange={e => setEditing({ ...editing, line: { ...editing.line, name: e.target.value } })}
        />
      </td>
      <td>
        <input
          type="number"
          aria-label={t('householdCosts.amount')}
          value={editing.line.amount}
          onChange={e => setEditing({ ...editing, line: { ...editing.line, amount: e.target.value } })}
        />
      </td>
      <td>
        <select
          aria-label={t('householdCosts.period')}
          value={editing.line.period}
          onChange={e => setEditing({ ...editing, line: { ...editing.line, period: e.target.value } })}
        >
          <option value="Monthly">{t('householdCosts.monthly')}</option>
          <option value="Annual">{t('householdCosts.annual')}</option>
        </select>
      </td>
      <td>
        <select
          aria-label={t('householdCosts.splitRule')}
          value={editing.line.splitRule}
          onChange={e => setEditing({ ...editing, line: { ...editing.line, splitRule: e.target.value } })}
        >
          {SPLIT_RULES.map(r => (
            <option key={r} value={r}>{t(`householdCosts.rule.${r}`)}</option>
          ))}
        </select>
      </td>
      <td colSpan={colSpan}>
        <label className="income-toggle">
          <input
            type="checkbox"
            checked={!!editing.line.isIncome}
            onChange={e => setEditing({ ...editing, line: { ...editing.line, isIncome: e.target.checked } })}
          />
          {t('householdCosts.isIncome')}
        </label>
      </td>
      <td className="row-actions">
        <button className="primary" disabled={busy} onClick={saveLine}>{t('common.save')}</button>
        <button disabled={busy} onClick={() => setEditing(null)}>{t('common.cancel')}</button>
      </td>
    </tr>
  );

  const lineTable = (lines, ownerships, propertyId) => {
    const isEditingHere = editing && editing.propertyId === propertyId;
    return (
      <table className="cost-table">
        <thead>
          <tr>
            <th>{t('householdCosts.line')}</th>
            <th className="num">{t('householdCosts.perMonth')}</th>
            <th>{t('householdCosts.period')}</th>
            <th>{t('householdCosts.splitRule')}</th>
            {people.map(p => <th key={p.id} className="num">{p.name}</th>)}
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map(l => (
            isEditingHere && editing.line.id === l.id
              ? <React.Fragment key={l.id}>{editorRow(propertyId, people.length)}</React.Fragment>
              : (
                <tr key={l.id} className={`line-row ${l.isIncome ? 'income' : ''}`}>
                  <td className="line-name">
                    {l.name}
                    {l.isIncome && <span className="income-tag">{t('householdCosts.income')}</span>}
                  </td>
                  <td className="num">{fmtNOK(l.monthly)}</td>
                  <td>{t(`householdCosts.${l.period === 'Annual' ? 'annual' : 'monthly'}`)}</td>
                  <td>{splitBadge(l.splitRule, ownerships)}</td>
                  {people.map(p => (
                    <td key={p.id} className={`num ${l.shares?.[p.id] < 0 ? 'surplus' : ''}`}>
                      {fmtNOK(l.shares?.[p.id])}
                    </td>
                  ))}
                  <td className="row-actions">
                    <button onClick={() => setEditing({ propertyId, line: { ...l } })}>
                      {t('common.edit')}
                    </button>
                    <button onClick={() => deleteLine(l, propertyId)}>{t('common.delete')}</button>
                  </td>
                </tr>
              )
          ))}
          {isEditingHere && !editing.line.id && editorRow(propertyId, people.length)}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={4 + people.length + 1}>
              <button
                className="add-line"
                onClick={() => setEditing({ propertyId, line: emptyLine() })}
              >
                + {t('householdCosts.addLine')}
              </button>
            </td>
          </tr>
        </tfoot>
      </table>
    );
  };

  if (loading) {
    return (
      <div className="household-costs">
        <AppHeader title={t('householdCosts.title')} subtitle={t('householdCosts.subtitle')} />
        <div className="hc-loading"><p>{t('common.loading')}</p></div>
      </div>
    );
  }

  const hasData = !!(summary && (summary.properties.length || summary.householdLines.length));

  return (
    <div className="household-costs">
      <AppHeader title={t('householdCosts.title')} subtitle={t('householdCosts.subtitle')} />

      {error && <div className="hc-error">{error}</div>}

      <div className="hc-tabs" role="tablist">
        {['overview', 'properties', 'household', 'groceries'].map(v => (
          <button
            key={v}
            role="tab"
            aria-selected={activeView === v}
            className={`hc-tab ${activeView === v ? 'active' : ''}`}
            onClick={() => setActiveView(v)}
          >
            {t(`householdCosts.tab.${v}`)}
          </button>
        ))}
      </div>

      {!hasData && activeView !== 'groceries' && (
        <div className="hc-empty">
          <p>{t('householdCosts.emptyIntro')}</p>
          <button className="primary" disabled={busy} onClick={() => handleSeed(false)}>
            {t('householdCosts.importSpreadsheet')}
          </button>
        </div>
      )}

      {activeView === 'overview' && hasData && (
        <>
          <div className="totals-grid">
            {people.map(p => (
              <div key={p.id} className="total-card">
                <div className="total-label">{p.name}</div>
                <div className="total-value">{fmtNOK(summary.totals[p.id])}</div>
                <div className="total-sub">{t('common.perMonth')}</div>
              </div>
            ))}
          </div>

          {summary.settlement && (
            <div className="settlement-card">
              <div className="settlement-text">
                {t('householdCosts.settlement', {
                  from: summary.settlement.fromName,
                  to: summary.settlement.toName
                })}
              </div>
              <div className="settlement-amount">{fmtNOK(summary.settlement.amount)}</div>
              <p className="settlement-note">{t('householdCosts.settlementNote')}</p>
            </div>
          )}

          <div className="property-summary">
            <h3>{t('householdCosts.byProperty')}</h3>
            <table className="cost-table">
              <thead>
                <tr>
                  <th>{t('householdCosts.property')}</th>
                  {people.map(p => <th key={p.id} className="num">{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {summary.properties.map(pr => (
                  <tr key={pr.propertyId}>
                    <td>{pr.name}</td>
                    {people.map(p => (
                      <td key={p.id} className={`num ${pr.shares?.[p.id] < 0 ? 'surplus' : ''}`}>
                        {fmtNOK(pr.shares?.[p.id])}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td>{t('householdCosts.tab.household')}</td>
                  {people.map(p => (
                    <td key={p.id} className="num">
                      {fmtNOK(summary.householdLines.reduce((s, l) => s + (l.shares?.[p.id] || 0), 0))}
                    </td>
                  ))}
                </tr>
                <tr className="subtotal">
                  <td>{t('householdCosts.total')}</td>
                  {people.map(p => (
                    <td key={p.id} className="num">{fmtNOK(summary.totals[p.id])}</td>
                  ))}
                </tr>
              </tbody>
            </table>
            <p className="hc-note">{t('householdCosts.surplusNote')}</p>
          </div>
        </>
      )}

      {activeView === 'properties' && hasData && (
        <div className="properties-view">
          {summary.properties.map(pr => (
            <section key={pr.propertyId} className="property-block">
              <div className="property-head">
                <h3>{pr.name}</h3>
                <div className="ownership-chips">
                  {pr.ownerships.map(o => {
                    const person = people.find(p => p.id === o.userId);
                    return (
                      <span key={o.userId} className="ownership-chip">
                        {person?.name}: {Math.round((o.ownershipFraction || 0) * 100)}%
                        {' · '}
                        {t('householdCosts.loanShare')} {Math.round((o.loanFraction || 0) * 100)}%
                      </span>
                    );
                  })}
                </div>
              </div>
              {lineTable(pr.lines, pr.ownerships, pr.propertyId)}
            </section>
          ))}
        </div>
      )}

      {activeView === 'household' && hasData && (
        <div className="household-view">
          <p className="hc-note">{t('householdCosts.householdIntro')}</p>
          {lineTable(summary.householdLines, null, null)}
        </div>
      )}

      {activeView === 'groceries' && (
        <div className="grocery-view">
          <div className="grocery-toolbar">
            <div className="grocery-period-controls">
              <label>
                {t('householdCosts.grocery.periodMode')}
                <select
                  value={groceryRange}
                  onChange={event => setGroceryRange(event.target.value)}
                >
                  <option value="month">{t('householdCosts.grocery.month')}</option>
                  <option value="ytd">{t('householdCosts.grocery.yearToDate')}</option>
                </select>
              </label>
              {groceryRange === 'month' ? (
                <label>
                  {t('householdCosts.grocery.period')}
                  <input
                    type="month"
                    value={groceryPeriod}
                    onChange={event => setGroceryPeriod(event.target.value)}
                  />
                </label>
              ) : (
                <label>
                  {t('householdCosts.grocery.year')}
                  <input
                    type="number"
                    min="2000"
                    max={currentYear()}
                    value={groceryYear}
                    onChange={event => setGroceryYear(event.target.value)}
                  />
                </label>
              )}
            </div>
            <div className="grocery-actions">
              <button disabled={busy} onClick={recategorizeGroceries}>
                {t('householdCosts.grocery.recategorize')}
              </button>
              <label className={`grocery-import ${busy ? 'disabled' : ''}`}>
                {busy ? t('householdCosts.grocery.importing') : t('householdCosts.grocery.import')}
                <input
                  type="file"
                  accept=".json,application/json"
                  multiple
                  disabled={busy}
                  onChange={importGroceries}
                />
              </label>
            </div>
          </div>

          <p className="hc-note">{t('householdCosts.grocery.importHelp')}</p>
          <details className="grocery-format">
            <summary>{t('householdCosts.grocery.format')}</summary>
            <pre>{GROCERY_IMPORT_EXAMPLE}</pre>
          </details>
          {groceryImport && (
            <div className="grocery-import-result">
              {t('householdCosts.grocery.importResult', groceryImport)}
            </div>
          )}
          {groceryRecategorized && (
            <div className="grocery-import-result">
              {t('householdCosts.grocery.recategorizeResult', groceryRecategorized)}
            </div>
          )}

          {groceryLoading ? (
            <div className="hc-loading"><p>{t('common.loading')}</p></div>
          ) : (
            <>
              <div className="totals-grid grocery-totals">
                <div className="total-card">
                  <div className="total-label">{t('householdCosts.grocery.total')}</div>
                  <div className="total-value">{fmtNOK(grocerySummary?.totalAmount)}</div>
                  <div className="total-sub">
                    {t('householdCosts.grocery.receipts', { count: grocerySummary?.receiptCount || 0 })}
                  </div>
                </div>
                <div className="total-card">
                  <div className="total-label">{t('householdCosts.grocery.discounts')}</div>
                  <div className="total-value">{fmtNOK(grocerySummary?.totalDiscount)}</div>
                  <div className="total-sub">
                    {t('householdCosts.grocery.items', { count: grocerySummary?.itemCount || 0 })}
                  </div>
                </div>
                <div className="total-card">
                  <div className="total-label">{t('householdCosts.grocery.uncategorized')}</div>
                  <div className="total-value">{fmtNOK(grocerySummary?.uncategorizedAmount)}</div>
                  <div className="total-sub">{t('householdCosts.grocery.reviewHint')}</div>
                </div>
              </div>

              <div className="grocery-grid">
                <section>
                  <h3>{t('householdCosts.grocery.byCategory')}</h3>
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>{t('householdCosts.grocery.category')}</th>
                        <th className="num">{t('householdCosts.amount')}</th>
                        <th className="num">{t('householdCosts.grocery.itemCount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(grocerySummary?.categories || []).map(category => (
                        <React.Fragment key={category.name}>
                          <tr className="grocery-category">
                            <td>{groceryLabel(category.name)}</td>
                            <td className="num">{fmtNOK(category.amount)}</td>
                            <td className="num">{category.itemCount}</td>
                          </tr>
                          {category.subcategories.map(subcategory => (
                            <tr key={`${category.name}-${subcategory.name}`} className="grocery-subcategory">
                              <td>{groceryLabel(subcategory.name)}</td>
                              <td className="num">{fmtNOK(subcategory.amount)}</td>
                              <td className="num">{subcategory.itemCount}</td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))}
                      {!grocerySummary?.categories?.length && (
                        <tr><td colSpan="3">{t('householdCosts.grocery.empty')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </section>

                <section>
                  <h3>{t('householdCosts.grocery.byRetailer')}</h3>
                  <table className="cost-table">
                    <thead>
                      <tr>
                        <th>{t('householdCosts.grocery.retailer')}</th>
                        <th className="num">{t('householdCosts.amount')}</th>
                        <th className="num">{t('householdCosts.grocery.receiptCount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(grocerySummary?.retailers || []).map(retailer => (
                        <tr key={retailer.retailer}>
                          <td>{t(`householdCosts.grocery.retailers.${retailer.retailer}`)}</td>
                          <td className="num">{fmtNOK(retailer.amount)}</td>
                          <td className="num">{retailer.receiptCount}</td>
                        </tr>
                      ))}
                      {!grocerySummary?.retailers?.length && (
                        <tr><td colSpan="3">{t('householdCosts.grocery.empty')}</td></tr>
                      )}
                    </tbody>
                  </table>
                </section>
              </div>
            </>
          )}
        </div>
      )}

      {hasData && activeView !== 'groceries' && (
        <div className="hc-footer">
          <button className="reimport" disabled={busy} onClick={() => handleSeed(true)}>
            {t('householdCosts.reimport')}
          </button>
        </div>
      )}
    </div>
  );
}

export default HouseholdCosts;
